import { ELEMENT_TYPES } from 'cg/components/documents/editor/slate/types'
import { Transforms, Path, Editor, Point, Range, Node, Element as SlateElement } from 'vendor/slate'
import { ReactEditor } from 'vendor/slate-react'
import { trimTabsAndNewLines } from 'cg/components/documents/helpers'
import { v4 as uuidv4 } from 'uuid'

const insertTabs = (editor, path) => {
  const id = `tab-${uuidv4()}`
  const firstTabId = `tab-${uuidv4()}-1`
  const secondTabId = `tab-${uuidv4()}-2`

  const tabs = {
    id,
    type: ELEMENT_TYPES.tabs,
    defaultActiveTab: firstTabId,
    children: [
      {
        type: ELEMENT_TYPES.tabs_header,
        parent: { type: ELEMENT_TYPES.tabs },
        children: [
          {
            type: ELEMENT_TYPES.tabs_header_item,
            tabId: firstTabId,
            parent: { type: ELEMENT_TYPES.tabs_header },
            children: [{ uuid: uuidv4(), text: 'Title', parent: { type: ELEMENT_TYPES.tabs_header_item } }]
          },
          {
            type: ELEMENT_TYPES.tabs_header_item,
            tabId: secondTabId,
            parent: { type: ELEMENT_TYPES.tabs_header },
            children: [{ uuid: uuidv4(), text: 'Title', parent: { type: ELEMENT_TYPES.tabs_header_item } }]
          }
        ]
      },
      {
        type: ELEMENT_TYPES.tabs_content_item,
        parent: { type: ELEMENT_TYPES.tabs },
        tabId: firstTabId,
        children: [
          {
            type: ELEMENT_TYPES.default,
            parent: { type: ELEMENT_TYPES.tabs_content_item },
            children: [{ uuid: uuidv4(), text: '', parent: { type: ELEMENT_TYPES.default } }]
          }
        ]
      },
      {
        type: ELEMENT_TYPES.tabs_content_item,
        parent: { type: ELEMENT_TYPES.tabs },
        tabId: secondTabId,
        children: [
          {
            type: ELEMENT_TYPES.default,
            parent: { type: ELEMENT_TYPES.tabs_content_item },
            children: [{ uuid: uuidv4(), text: '', parent: { type: ELEMENT_TYPES.default } }]
          }
        ]
      }
    ]
  }

  Transforms.insertNodes(editor, tabs, { at: path, select: true })
  ReactEditor.focus(editor)
}

const insertTab = (editor, tabHeaderPath, tabContentPath) => {
  const firstTabId = `tab-${uuidv4()}`
  const tabHeaderItem = {
    type: ELEMENT_TYPES.tabs_header_item,
    tabId: firstTabId,
    parent: { type: ELEMENT_TYPES.tabs_header },
    children: [{ text: 'Title', parent: { type: ELEMENT_TYPES.tabs_header_item } }]
  }
  const tabContentItem = {
    type: ELEMENT_TYPES.tabs_content_item,
    tabId: firstTabId,
    parent: { type: ELEMENT_TYPES.tabs },
    children: [
      {
        type: ELEMENT_TYPES.default,
        parent: { type: ELEMENT_TYPES.tabs_content_item },
        children: [{ text: '', parent: { type: ELEMENT_TYPES.default } }]
      }
    ]
  }

  Transforms.insertNodes(editor, tabHeaderItem, { at: Path.next(tabHeaderPath) })
  Transforms.insertNodes(editor, tabContentItem, { at: Path.next(tabContentPath) })
}

const withTabs = editor => {
  const { insertBreak, deleteBackward, deleteForward, deleteFragment, insertText, insertData } = editor

  // Prevent breaks on tab header item
  editor.insertBreak = () => {
    const { selection } = editor

    if (selection) {
      const [match] = Editor.nodes(editor, {
        at: selection,
        mode: 'lowest',
        match: n => !Editor.isEditor(n) && n.parent && n.parent.type === ELEMENT_TYPES.tabs_header_item
      })

      if (match) {
        return
      }
    }

    insertBreak()
  }

  // Prevent node deletion on tab content and header items
  editor.deleteBackward = unit => {
    const { selection } = editor

    if (selection && Range.isCollapsed(selection)) {
      const { anchor } = selection
      const [match] = Editor.nodes(editor, {
        at: selection,
        mode: 'lowest',
        match: n =>
          !Editor.isEditor(n) &&
          [ELEMENT_TYPES.tabs_header_item, ELEMENT_TYPES.tabs_content_item].includes(n.parent && n.parent.type)
      })

      if (match) {
        const [, path] = match
        const start = Editor.start(editor, Path.parent(path))

        if (Point.equals(anchor, start)) {
          return
        }
      }
    }

    deleteBackward(unit)
  }

  // Prevent node deletion on tab content and header items
  editor.deleteForward = unit => {
    const { selection } = editor

    if (selection && Range.isCollapsed(selection)) {
      const { anchor } = selection
      const [match] = Editor.nodes(editor, {
        at: selection,
        mode: 'lowest',
        match: n =>
          !Editor.isEditor(n) &&
          [ELEMENT_TYPES.tabs_content_item, ELEMENT_TYPES.tabs_header_item].includes(n.parent && n.parent.type)
      })

      if (match) {
        const [node, path] = match
        if (node) {
          const end = Editor.end(editor, Path.parent(path))

          if (Point.equals(anchor, end)) {
            return
          }
        }
      }
    }

    deleteForward(unit)
  }

  editor.deleteFragment = () => {
    const { selection } = editor

    if (selection) {
      const [match] = Editor.nodes(editor, {
        at: selection,
        mode: 'lowest',
        match: n =>
          !Editor.isEditor(n) &&
          [ELEMENT_TYPES.tabs_header_item, ELEMENT_TYPES.tabs_content_item].includes(n.parent && n.parent.type)
      })

      if (match) {
        const [start, end] = Range.edges(selection)
        if (start.path.length !== end.path.length) return
        const startPathNode = Node.parent(editor, start.path)
        const endPathNode = Node.parent(editor, end.path)

        if (startPathNode.parent && endPathNode.parent && startPathNode.parent.type !== endPathNode.parent.type) return
      }
    }
    deleteFragment()
  }

  editor.insertText = text => {
    const { selection } = editor
    if (selection) {
      const [match] = Editor.nodes(editor, {
        at: selection,
        mode: 'lowest',
        match: n =>
          !Editor.isEditor(n) &&
          [ELEMENT_TYPES.tabs_header_item, ELEMENT_TYPES.tabs_content_item].includes(n.parent && n.parent.type)
      })

      if (match) {
        const [start, end] = Range.edges(selection)
        if (start.path.length !== end.path.length) return
        const startPathNode = Node.parent(editor, start.path)
        const endPathNode = Node.parent(editor, end.path)

        if (startPathNode.parent && endPathNode.parent && startPathNode.parent.type !== endPathNode.parent.type) return
      }
    }

    insertText(text)
  }

  editor.insertData = data => {
    const { selection } = editor

    if (selection) {
      const [match] = Editor.nodes(editor, {
        at: selection,
        mode: 'lowest',
        match: n =>
          !Editor.isEditor(n) &&
          [ELEMENT_TYPES.tabs_header_item, ELEMENT_TYPES.tabs_content_item].includes(n.parent && n.parent.type)
      })

      if (match) {
        const [start, end] = Range.edges(selection)
        if (start.path.length !== end.path.length) return
        const startPathNode = Node.parent(editor, start.path)
        const endPathNode = Node.parent(editor, end.path)

        if (startPathNode.parent && endPathNode.parent && startPathNode.parent.type !== endPathNode.parent.type) return

        const [node] = match

        if (node && node.parent && node.parent.type === ELEMENT_TYPES.tabs_header_item) {
          insertText(trimTabsAndNewLines(data.getData('text/plain')))
          return
        }
      }
    }

    insertData(data)
  }

  return editor
}
export { insertTabs, insertTab, withTabs }
